import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { formatOfficialDateBR, officialDateKey } from '../utils/officialDate';
import { groupLawTypeOptions, LawTypeOption } from '../utils/lawTypeFilter';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Card,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Divider,
  Tabs,
  Tab,
  alpha,
  useTheme,
  TextField,
  Link,
} from '@mui/material';
import {
  FilterAlt,
  Check,
  MoreVert,
  OpenInNew,
  Email,
  ArrowUpward,
  ArrowDownward,
  SwapVert,
} from '@mui/icons-material';
import { ActionCell } from './ActionCell';
import { ChangesActionsColumnHeader } from './greening/ChangesActionsColumnHeader';
import { ChangeMonitoringBadges } from './monitoring/ChangeMonitoringBadges';
import { AgencyFilterPanel } from './agencyFilter';
import type { ChangesAgencyFilterCatalog } from '../types/changesAgencyFilter';
import { resolveChangeAssuntoDisplay } from '../utils/changeAssunto';
import { normalizeAssuntoKey } from '../utils/assuntoMonitoring';
import { changeTableRowId } from '../utils/changeTableFocus';

// Tipos
export interface Change {
  id: number;
  legislation_id?: number;
  state: string;
  agency: string;
  /** Vínculos múltiplos (só preenchido quando há mais de uma agência); exibir no modal de detalhes. */
  agencies_linked?: string | null;
  law: string;
  law_type?: string | null;
  title: string;
  date: string;
  published_at?: string | null;
  effective_date: string | null;
  vigency_status?: string | null;
  change: string;
  subject?: string | null;
  area_tematica?: string | null;
  url?: string | null;
  dou_ref?: string | null;
  summary?: string | null;
  is_assunto_monitored?: boolean;
  monitored_assunto_label?: string | null;
  is_norm_monitored?: boolean;
  is_keyword_monitored?: boolean;
  monitored_keyword_label?: string | null;
  is_agency_monitored?: boolean;
  monitored_agency_label?: string | null;
  greening_status?: string | null;
  greening_questions_count?: number;
}

export type ChangesTableColumnId =
  | 'state'
  | 'agency'
  | 'lawType'
  | 'lawNumber'
  | 'title'
  | 'detectedDate'
  | 'publicationDate'
  | 'effectiveDate'
  | 'changeType';

export type ChangesColumnFilterKey =
  | 'state'
  | 'agency'
  | 'lawType'
  | 'detectedDate'
  | 'publicationDate'
  | 'effectiveDate'
  | 'changeType';

/** Estado “Todos” nos filtros de coluna (reutilizável ao limpar filtros). */
export const EMPTY_CHANGES_COLUMN_FILTERS: Record<ChangesColumnFilterKey, string[]> = {
  state: [],
  agency: [],
  lawType: [],
  detectedDate: [],
  publicationDate: [],
  effectiveDate: [],
  changeType: [],
};

export interface TableColumn {
  id: ChangesTableColumnId;
  label: string;
  filterable?: boolean;
  sortable?: boolean;
  width?: string;
}

export interface ChangesTableProps {
  data: Change[];
  columns?: TableColumn[];
  loading?: boolean;
  onRowClick?: (change: Change) => void;
  onViewDetails?: (change: Change) => void;
  onSendEmail?: (emailData: { to: string; subject: string; item: any }) => Promise<void>;
  showActions?: boolean;
  dense?: boolean;
  stickyHeader?: boolean;
  monitoringStatus?: { [key: string | number]: boolean }; // legislationId -> isMonitored
  showMonitoringBadge?: boolean;
  /** Chaves de assuntos gravados em monitoramento (fonte: GET /assuntos/monitoring). */
  monitoredAssuntoLabels?: ReadonlyMap<string, string>;
  /** Barra de filtros rápida (principalmente para o Dashboard). */
  quickFilters?: {
    enabled?: boolean;
    number?: boolean;
    title?: boolean;
    publicationDateRange?: boolean;
    effectiveDateRange?: boolean;
    /** Se fornecido, o estado vira controlado (ideal p/ filtro no servidor). */
    values?: {
      numberQuery?: string;
      titleQuery?: string;
      publicationStart?: string;
      publicationEnd?: string;
      effectiveStart?: string;
      effectiveEnd?: string;
    };
    /** Callback para subir alterações de filtros (server-side). */
    onChange?: (values: {
      numberQuery: string;
      titleQuery: string;
      publicationStart: string;
      publicationEnd: string;
      effectiveStart: string;
      effectiveEnd: string;
    }) => void;
  };
  modalProps?: {
    showEmail?: boolean;
    showDownload?: boolean;
    showHistory?: boolean;
    showMonitoring?: boolean;
  };
  pagination?: {
    enabled: boolean;
    page: number;
    rowsPerPage: number;
    totalRows: number;
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
  };
  /** Ordenação no servidor (GET /changes). Sem isso, ordena no cliente sobre `data`. */
  serverSort?: {
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    onRequestSort: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  };
  /** Opções fixas do filtro da coluna Estado (Federal + UFs + capitais do catálogo). */
  stateFilterOptions?: string[];
  /** Catálogo do filtro Agência (Fontes ativas: federal canônico / estaduais por UF / municipais). */
  agencyFilterCatalog?: ChangesAgencyFilterCatalog;
  /** Opções fixas do filtro da coluna Tipo de Norma (catálogo). */
  lawTypeFilterOptions?: string[];
  /** Opções fixas do filtro da coluna Tipo de Mudança (catálogo). */
  changeTypeFilterOptions?: string[];
  /**
   * Quando definido, o filtro de Estado não é reaplicado no cliente (dados paginados no servidor)
   * e o pai pode enviar `states` ao GET /changes.
   */
  onColumnFiltersChange?: (filters: Record<ChangesColumnFilterKey, string[]>) => void;
  /** Sincroniza seleções de coluna a partir do pai (dashboard: chips / limpar tudo). */
  controlledColumnFilters?: Record<ChangesColumnFilterKey, string[]>;
  /** Filtro da coluna Estado controlado pelo pai (ex.: página de mudanças + métricas). */
  controlledStateFilter?: string[];
  onControlledStateFilterChange?: (states: string[]) => void;
  /**
   * Incrementar para limpar todas as seleções de filtros de coluna (ex.: “Limpar” no painel da página Mudanças).
   * O botão “Limpar filtros” da barra rápida já zera colunas internamente.
   */
  resetColumnFiltersNonce?: number;
  /** Destaca a linha da norma (ex.: vinda do sininho). */
  highlightedChangeId?: number | null;
  /** Filtros no cabeçalho da coluna Ações (server-side via GET /changes). */
  actionsColumnFilters?: {
    monitoredNormAssuntoActive: boolean;
    monitoredAgencyActive: boolean;
    greeningQaActive: boolean;
    onMonitoredNormAssuntoToggle: () => void;
    onMonitoredAgencyToggle: () => void;
    onGreeningQaToggle: () => void;
  };
}

const COLUMN_TO_API_SORT: Record<ChangesTableColumnId, string> = {
  state: 'state',
  agency: 'agency',
  lawType: 'law_type',
  lawNumber: 'law_number',
  title: 'title',
  detectedDate: 'detected_at',
  publicationDate: 'published_at',
  effectiveDate: 'effective_date',
  changeType: 'change_type',
};

const DATE_SORT_IDS: ChangesTableColumnId[] = ['detectedDate', 'publicationDate', 'effectiveDate'];

function dateFilterKey(iso: string | null | undefined): string {
  if (!iso) return 'N/A';
  const key = officialDateKey(iso);
  return key ?? 'N/A';
}

function formatDateMenuLabel(key: string): string {
  if (key === 'N/A') return 'Sem data';
  if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
    return formatOfficialDateBR(key);
  }
  return formatOfficialDateBR(key) || key;
}

const ACTIONS_COLUMN_WIDTH = '208px';

const HEADER_LABEL_SX = {
  fontWeight: 700,
  fontSize: '0.75rem',
  lineHeight: 1.25,
  display: 'block',
  whiteSpace: 'normal' as const,
  wordBreak: 'break-word' as const,
};

/** Cabeçalho em duas linhas: nome/ordenação em cima, filtro embaixo. */
function getHeaderCellSx(column: TableColumn, compact: boolean) {
  const py = compact ? 0.75 : 1;
  if (column.id === 'title') {
    return {
      px: 1,
      py,
      verticalAlign: 'top' as const,
      overflow: 'visible',
      width: 'auto',
      minWidth: 200,
    };
  }
  const w = column.width;
  return {
    width: w,
    minWidth: w,
    px: 0.75,
    py,
    verticalAlign: 'top' as const,
    overflow: 'visible',
  };
}

const DATE_COLUMN_IDS = new Set<ChangesTableColumnId>([
  'detectedDate',
  'publicationDate',
  'effectiveDate',
]);

/** Colunas fixas; título flexível absorve o espaço restante. */
const DEFAULT_COLUMNS: TableColumn[] = [
  { id: 'state', label: 'Estado', filterable: true, sortable: true, width: '86px' },
  { id: 'agency', label: 'Agência', filterable: true, sortable: true, width: '96px' },
  { id: 'lawType', label: 'Tipo', filterable: true, sortable: true, width: '92px' },
  { id: 'lawNumber', label: 'Número', sortable: true, width: '94px' },
  { id: 'title', label: 'Título', sortable: true },
  { id: 'detectedDate', label: 'Detectada', filterable: true, sortable: true, width: '94px' },
  { id: 'publicationDate', label: 'Publicação', filterable: true, sortable: true, width: '108px' },
  { id: 'effectiveDate', label: 'Vigência', filterable: true, sortable: true, width: '96px' },
  { id: 'changeType', label: 'Mudança', filterable: true, sortable: true, width: '96px' },
];

const COMPACT_CHIP_SX = {
  maxWidth: '100%',
  height: 22,
  '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
} as const;

/** Corpo: overflow oculto para ellipsis em título e chips. */
function getBodyCellSx(column: TableColumn, compact: boolean) {
  const py = compact ? 0.5 : 0.75;
  if (column.id === 'title') {
    return {
      px: 1,
      py,
      overflow: 'hidden',
      width: 'auto',
      minWidth: 200,
    };
  }
  const w = column.width;
  const tight = {
    width: w,
    minWidth: w,
    px: 0.75,
    py,
    overflow: 'hidden' as const,
  };
  if (DATE_COLUMN_IDS.has(column.id)) {
    return { ...tight, whiteSpace: 'nowrap' as const, fontSize: '0.8125rem' };
  }
  return tight;
}

function compareChanges(
  a: Change,
  b: Change,
  column: ChangesTableColumnId,
  order: 'asc' | 'desc',
  parseLaw: (s: string) => { type: string; number: string },
): number {
  const dir = order === 'asc' ? 1 : -1;
  const pa = parseLaw(a.law);
  const pb = parseLaw(b.law);
  const ts = (iso: string | null | undefined) => {
    if (!iso) return 0;
    const t = new Date(iso).getTime();
    return Number.isNaN(t) ? 0 : t;
  };
  switch (column) {
    case 'state':
      return dir * a.state.localeCompare(b.state, 'pt-BR');
    case 'agency':
      return dir * a.agency.localeCompare(b.agency, 'pt-BR');
    case 'lawType':
      return dir * pa.type.localeCompare(pb.type, 'pt-BR');
    case 'lawNumber':
      return dir * pa.number.localeCompare(pb.number, 'pt-BR', { numeric: true });
    case 'title':
      return dir * (a.title || '').localeCompare(b.title || '', 'pt-BR');
    case 'detectedDate':
      return dir * (ts(a.date) - ts(b.date));
    case 'publicationDate':
      return dir * (ts(a.published_at) - ts(b.published_at));
    case 'effectiveDate':
      return dir * (ts(a.effective_date) - ts(b.effective_date));
    case 'changeType':
      return dir * a.change.localeCompare(b.change, 'pt-BR');
    default:
      return dir * (ts(a.date) - ts(b.date));
  }
}

export function ChangesTable({
  data,
  columns = DEFAULT_COLUMNS,
  loading = false,
  onRowClick,
  onViewDetails,
  onSendEmail,
  showActions = true,
  dense = false,
  stickyHeader = false,
  monitoringStatus = {},
  showMonitoringBadge = false,
  monitoredAssuntoLabels,
  quickFilters,
  pagination,
  modalProps,
  serverSort,
  stateFilterOptions,
  agencyFilterCatalog,
  lawTypeFilterOptions,
  changeTypeFilterOptions,
  onColumnFiltersChange,
  controlledColumnFilters,
  controlledStateFilter,
  onControlledStateFilterChange,
  resetColumnFiltersNonce,
  highlightedChangeId = null,
  actionsColumnFilters,
}: ChangesTableProps) {
  const theme = useTheme();

  const [localSortColumn, setLocalSortColumn] = useState<ChangesTableColumnId>('publicationDate');
  const [localSortOrder, setLocalSortOrder] = useState<'asc' | 'desc'>('desc');

  // Filtros rápidos (texto + ranges de datas)
  const quickEnabled = Boolean(quickFilters?.enabled);
  const showQuickNumber = quickEnabled && (quickFilters?.number ?? true);
  const showQuickTitle = quickEnabled && (quickFilters?.title ?? true);
  const showQuickPublicationRange = quickEnabled && (quickFilters?.publicationDateRange ?? true);
    const showQuickEffectiveRange = quickEnabled && (quickFilters?.effectiveDateRange ?? false);

  const isQuickControlled = Boolean(quickFilters?.onChange);
  const [quickNumberQueryLocal, setQuickNumberQueryLocal] = useState('');
  const [quickTitleQueryLocal, setQuickTitleQueryLocal] = useState('');
  const [quickPublicationStartLocal, setQuickPublicationStartLocal] = useState('');
  const [quickPublicationEndLocal, setQuickPublicationEndLocal] = useState('');
  const [quickEffectiveStartLocal, setQuickEffectiveStartLocal] = useState('');
  const [quickEffectiveEndLocal, setQuickEffectiveEndLocal] = useState('');

  const quickNumberQuery = (quickFilters?.values?.numberQuery ?? quickNumberQueryLocal) ?? '';
  const quickTitleQuery = (quickFilters?.values?.titleQuery ?? quickTitleQueryLocal) ?? '';
  const quickPublicationStart = (quickFilters?.values?.publicationStart ?? quickPublicationStartLocal) ?? '';
  const quickPublicationEnd = (quickFilters?.values?.publicationEnd ?? quickPublicationEndLocal) ?? '';
  const quickEffectiveStart = (quickFilters?.values?.effectiveStart ?? quickEffectiveStartLocal) ?? '';
  const quickEffectiveEnd = (quickFilters?.values?.effectiveEnd ?? quickEffectiveEndLocal) ?? '';

  const setQuickValues = useCallback(
    (patch: Partial<{
      numberQuery: string;
      titleQuery: string;
      publicationStart: string;
      publicationEnd: string;
      effectiveStart: string;
      effectiveEnd: string;
    }>) => {
      if (quickFilters?.onChange) {
        quickFilters.onChange({
          numberQuery: quickNumberQuery,
          titleQuery: quickTitleQuery,
          publicationStart: quickPublicationStart,
          publicationEnd: quickPublicationEnd,
          effectiveStart: quickEffectiveStart,
          effectiveEnd: quickEffectiveEnd,
          ...patch,
        });
        return;
      }
      if (patch.numberQuery !== undefined) setQuickNumberQueryLocal(patch.numberQuery);
      if (patch.titleQuery !== undefined) setQuickTitleQueryLocal(patch.titleQuery);
      if (patch.publicationStart !== undefined) setQuickPublicationStartLocal(patch.publicationStart);
      if (patch.publicationEnd !== undefined) setQuickPublicationEndLocal(patch.publicationEnd);
      if (patch.effectiveStart !== undefined) setQuickEffectiveStartLocal(patch.effectiveStart);
      if (patch.effectiveEnd !== undefined) setQuickEffectiveEndLocal(patch.effectiveEnd);
    },
    [
      quickFilters,
      quickNumberQuery,
      quickTitleQuery,
      quickPublicationStart,
      quickPublicationEnd,
      quickEffectiveStart,
      quickEffectiveEnd,
    ],
  );

  // Estados para filtros de coluna
  const [columnFilterAnchor, setColumnFilterAnchor] = useState<{ [key: string]: HTMLElement | null }>({});
  const [columnFilters, setColumnFilters] = useState<Record<ChangesColumnFilterKey, string[]>>(() => ({
    ...EMPTY_CHANGES_COLUMN_FILTERS,
  }));
  const [stateFilterTab, setStateFilterTab] = useState<'federacao' | 'estados' | 'municipios'>('federacao');
  const skipColumnFilterNotifyRef = useRef(false);

  useEffect(() => {
    if (!controlledColumnFilters) return;
    skipColumnFilterNotifyRef.current = true;
    setColumnFilters(controlledColumnFilters);
  }, [controlledColumnFilters]);

  useEffect(() => {
    if (!onColumnFiltersChange) return;
    if (skipColumnFilterNotifyRef.current) {
      skipColumnFilterNotifyRef.current = false;
      return;
    }
    onColumnFiltersChange({
      ...columnFilters,
      state: controlledStateFilter !== undefined ? controlledStateFilter : columnFilters.state,
    });
  }, [columnFilters, controlledStateFilter, onColumnFiltersChange]);

  useEffect(() => {
    if (controlledStateFilter === undefined) return;
    setColumnFilters((prev) => ({ ...prev, state: controlledStateFilter }));
  }, [controlledStateFilter]);

  const clearAllColumnFilterSelections = useCallback(() => {
    onControlledStateFilterChange?.([]);
    setColumnFilters({ ...EMPTY_CHANGES_COLUMN_FILTERS });
    setStateFilterTab('federacao');
    setColumnFilterAnchor({});
  }, [onControlledStateFilterChange]);

  const resetNonceSeen = useRef<number | undefined>(undefined);
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;
  useEffect(() => {
    if (resetColumnFiltersNonce === undefined) return;
    if (resetNonceSeen.current === undefined) {
      resetNonceSeen.current = resetColumnFiltersNonce;
      return;
    }
    if (resetColumnFiltersNonce === resetNonceSeen.current) return;
    resetNonceSeen.current = resetColumnFiltersNonce;
    clearAllColumnFilterSelections();
    const p = paginationRef.current;
    if (p?.enabled) p.onPageChange(1);
  }, [resetColumnFiltersNonce, clearAllColumnFilterSelections]);

  // Função para parsing de legislação
  const parseLegislationData = (lawString: string) => {
    if (!lawString) return { type: '', number: '' };

    const match = lawString.match(/^(Lei|Decreto|Portaria|Resolução|Instrução Normativa|Norma|IN|Medida Provisória|MP|Ordem de Serviço|OS)\s*(.+)$/i);

    if (match) {
      let type = match[1];
      const number = match[2].trim();

      const typeUpper = type.toUpperCase();
      if (typeUpper === 'IN') type = 'Instrução Normativa';
      if (typeUpper === 'MP') type = 'Medida Provisória';
      if (typeUpper === 'OS') type = 'Ordem de Serviço';

      type = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
      return { type, number };
    }

    return { type: 'Norma', number: lawString };
  };

  // Função para determinar se uma mudança está relacionada a legislação monitorada
  const isChangeFromMonitoredLegislation = (change: Change): boolean => {
    if (!showMonitoringBadge || !monitoringStatus) return false;
    return monitoringStatus[change.id] === true;
  };

  const isChangeAssuntoMonitored = (change: Change): boolean => {
    if (change.is_assunto_monitored) return true;
    if (!monitoredAssuntoLabels || monitoredAssuntoLabels.size === 0) return false;
    const assunto = resolveChangeAssuntoDisplay(change.subject, change.area_tematica);
    if (!assunto) return false;
    return monitoredAssuntoLabels.has(normalizeAssuntoKey(assunto));
  };

  const getMonitoredAssuntoLabel = (change: Change): string | null => {
    if (change.monitored_assunto_label) return change.monitored_assunto_label;
    if (!monitoredAssuntoLabels || monitoredAssuntoLabels.size === 0) return null;
    const assunto = resolveChangeAssuntoDisplay(change.subject, change.area_tematica);
    if (!assunto) return null;
    return monitoredAssuntoLabels.get(normalizeAssuntoKey(assunto)) ?? null;
  };

  const hasMonitoringFlagsFromApi = (change: Change): boolean =>
    change.is_assunto_monitored === true ||
    change.is_norm_monitored === true ||
    change.is_keyword_monitored === true ||
    change.is_agency_monitored === true;

  // Função para cor da legislação
  const getLegislationColor = (type: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    const typeUpper = type.toUpperCase();
    if (typeUpper.includes('LEI')) return 'primary';
    if (typeUpper.includes('DECRETO')) return 'secondary';
    if (typeUpper.includes('PORTARIA')) return 'info';
    if (typeUpper.includes('RESOLUÇÃO')) return 'success';
    if (typeUpper.includes('INSTRUÇÃO')) return 'warning';
    if (typeUpper.includes('MEDIDA')) return 'error';
    return 'default';
  };

  // Função para cor do tipo de mudança
  const getChangeTypeColor = (changeType: string): 'success' | 'info' | 'error' | 'warning' | 'default' => {
    const type = changeType.toLowerCase();
    if (type.includes('criad')) return 'success';
    if (type.includes('atualizad')) return 'info';
    if (type.includes('revogad')) return 'error';
    if (type.includes('suspend')) return 'warning';
    return 'default';
  };

  // Handlers de filtro de coluna
  const handleColumnFilterOpen = (column: string, event: React.MouseEvent<HTMLElement>) => {
    setColumnFilterAnchor(prev => ({ ...prev, [column]: event.currentTarget }));
  };

  const handleColumnFilterClose = (column: string) => {
    setColumnFilterAnchor(prev => ({ ...prev, [column]: null }));
  };

  const serverSideColumnFiltering = Boolean(onColumnFiltersChange);

  const handleLawTypeToggle = (values: string[]) => {
    setColumnFilters((prev) => {
      const current = prev.lawType;
      const active = values.some((value) => current.includes(value));
      const next = active
        ? current.filter((value) => !values.includes(value))
        : current.concat(values.filter((value) => !current.includes(value)));
      return { ...prev, lawType: next };
    });
  };

  const handleColumnFilterToggle = (column: ChangesColumnFilterKey, value: string) => {
    if (column === 'state' && onControlledStateFilterChange) {
      const current = controlledStateFilter ?? columnFilters.state;
      const newValues = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      onControlledStateFilterChange(newValues);
      setColumnFilters((prev) => ({ ...prev, state: newValues }));
      return;
    }
    setColumnFilters((prev) => {
      const current = prev[column];
      const newValues = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [column]: newValues };
    });
  };

  const handleClearColumnFilter = (column: ChangesColumnFilterKey) => {
    if (column === 'state' && onControlledStateFilterChange) {
      onControlledStateFilterChange([]);
      setColumnFilters((prev) => ({ ...prev, state: [] }));
      handleColumnFilterClose(column);
      return;
    }
    setColumnFilters((prev) => ({ ...prev, [column]: [] }));
    handleColumnFilterClose(column);
  };

  const defaultSortOrderForColumn = useCallback((col: ChangesTableColumnId): 'asc' | 'desc' => {
    return DATE_SORT_IDS.includes(col) ? 'desc' : 'asc';
  }, []);

  const handleRequestSort = useCallback(
    (columnId: ChangesTableColumnId) => {
      const apiKey = COLUMN_TO_API_SORT[columnId];
      if (serverSort) {
        if (serverSort.sortBy === apiKey) {
          serverSort.onRequestSort(apiKey, serverSort.sortOrder === 'desc' ? 'asc' : 'desc');
        } else {
          serverSort.onRequestSort(apiKey, defaultSortOrderForColumn(columnId));
        }
        return;
      }
      if (localSortColumn === columnId) {
        setLocalSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'));
      } else {
        setLocalSortColumn(columnId);
        setLocalSortOrder(defaultSortOrderForColumn(columnId));
      }
    },
    [serverSort, defaultSortOrderForColumn, localSortColumn],
  );


  const FILTER_KEY_BY_COLUMN: Partial<Record<ChangesTableColumnId, ChangesColumnFilterKey>> = {
    state: 'state',
    agency: 'agency',
    lawType: 'lawType',
    detectedDate: 'detectedDate',
    publicationDate: 'publicationDate',
    effectiveDate: 'effectiveDate',
    changeType: 'changeType',
  };

  // Extrair valores únicos para filtros
  const getUniqueColumnValues = (column: ChangesColumnFilterKey) => {
    const values = new Set<string>();

    data.forEach((change) => {
      if (column === 'state' && change.state) {
        values.add(change.state);
      } else if (column === 'agency' && change.agency) {
        values.add(change.agency);
      } else if (column === 'lawType') {
        const t = change.law_type ?? parseLegislationData(change.law).type;
        if (t) values.add(t);
      } else if (column === 'changeType' && change.change) {
        values.add(change.change);
      } else if (column === 'detectedDate') {
        values.add(dateFilterKey(change.date));
      } else if (column === 'publicationDate') {
        values.add(dateFilterKey(change.published_at));
      } else if (column === 'effectiveDate') {
        values.add(dateFilterKey(change.effective_date));
      }
    });

    return Array.from(values).sort((a, b) => {
      if (a === 'N/A') return 1;
      if (b === 'N/A') return -1;
      return a.localeCompare(b);
    });
  };

  // Filtrar dados
  const filteredData = useMemo(() => {
    // Quando os filtros rápidos são controlados, assume-se filtro no servidor.
    // Evita "double filtering" no cliente sobre apenas a página atual.
    const applyQuickClientSide = quickEnabled && !isQuickControlled;

    const numberNeedle = quickNumberQuery.trim().toLowerCase();
    const titleNeedle = quickTitleQuery.trim().toLowerCase();

    const withinRange = (iso: string | null | undefined, start: string, end: string) => {
      if (!start && !end) return true;
      if (!iso) return false;
      const key = dateFilterKey(iso);
      if (key === 'N/A') return false;
      if (start && key < start) return false;
      if (end && key > end) return false;
      return true;
    };

    return data.filter((change) => {
      if (applyQuickClientSide) {
        if (showQuickNumber) {
          if (numberNeedle) {
            const { number } = parseLegislationData(change.law);
            if (!String(number ?? '').toLowerCase().includes(numberNeedle)) return false;
          }
        }
        if (showQuickTitle) {
          if (titleNeedle) {
            if (!String(change.title ?? '').toLowerCase().includes(titleNeedle)) return false;
          }
        }
        if (showQuickPublicationRange) {
          if (!withinRange(change.published_at, quickPublicationStart, quickPublicationEnd)) return false;
        }
        if (showQuickEffectiveRange) {
          if (!withinRange(change.effective_date, quickEffectiveStart, quickEffectiveEnd)) return false;
        }
      }

      if (!serverSideColumnFiltering) {
        if (columnFilters.state.length > 0 && !columnFilters.state.includes(change.state)) {
          return false;
        }

        if (columnFilters.agency.length > 0 && !columnFilters.agency.includes(change.agency)) {
          return false;
        }

        if (columnFilters.lawType.length > 0) {
          const t = change.law_type ?? parseLegislationData(change.law).type;
          if (!columnFilters.lawType.includes(t)) {
            return false;
          }
        }

        if (columnFilters.changeType.length > 0 && !columnFilters.changeType.includes(change.change)) {
          return false;
        }
      }

      if (columnFilters.detectedDate.length > 0 && !columnFilters.detectedDate.includes(dateFilterKey(change.date))) {
        return false;
      }

      if (
        columnFilters.publicationDate.length > 0 &&
        !columnFilters.publicationDate.includes(dateFilterKey(change.published_at))
      ) {
        return false;
      }

      if (
        columnFilters.effectiveDate.length > 0 &&
        !columnFilters.effectiveDate.includes(dateFilterKey(change.effective_date))
      ) {
        return false;
      }

      return true;
    });
  }, [
    data,
    columnFilters,
    quickEnabled,
    isQuickControlled,
    showQuickNumber,
    showQuickTitle,
    showQuickPublicationRange,
    showQuickEffectiveRange,
    quickNumberQuery,
    quickTitleQuery,
    quickPublicationStart,
    quickPublicationEnd,
    quickEffectiveStart,
    quickEffectiveEnd,
    serverSideColumnFiltering,
  ]);

  const activeSortColumn: ChangesTableColumnId = serverSort
    ? (() => {
        const api = serverSort.sortBy;
        const norm =
          api === 'date' ? 'detected_at' : api === 'source' ? 'agency' : api === 'type' ? 'law_type' : api;
        const col = (Object.keys(COLUMN_TO_API_SORT) as ChangesTableColumnId[]).find(
          (k) => COLUMN_TO_API_SORT[k] === norm,
        );
        return col ?? 'publicationDate';
      })()
    : localSortColumn;

  const activeSortOrder: 'asc' | 'desc' = serverSort ? serverSort.sortOrder : localSortOrder;

  const processedData = useMemo(() => {
    if (serverSort) {
      return filteredData;
    }
    const out = [...filteredData];
    out.sort((a, b) => compareChanges(a, b, activeSortColumn, activeSortOrder, parseLegislationData));
    return out;
  }, [filteredData, serverSort, activeSortColumn, activeSortOrder]);

  // Função de render (NÃO um componente): assim o cabeçalho é reconciliado no
  // lugar a cada render em vez de remontar. Como componente interno recriado a
  // cada render, o React trocava o nó DOM do ícone de filtro no clique e o
  // anchorEl do menu virava nó órfão → menu abria no canto (0,0).
  const renderColumnHeader = (column: TableColumn) => {
    const filterKey = FILTER_KEY_BY_COLUMN[column.id];
    const filterActiveLen =
      filterKey === 'state'
        ? (controlledStateFilter !== undefined ? controlledStateFilter : columnFilters.state).length
        : filterKey
          ? columnFilters[filterKey].length
          : 0;
    const isFiltered = filterActiveLen > 0;
    const isSortActive = activeSortColumn === column.id;
    const showControls = column.sortable || (column.filterable && filterKey);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.125, width: '100%' }}>
        <Box component="span" sx={HEADER_LABEL_SX}>
          {column.label}
        </Box>
        {showControls ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, ml: -0.5, minHeight: 26 }}>
            {column.sortable ? (
              <IconButton
                size="small"
                onClick={() => handleRequestSort(column.id)}
                sx={{
                  p: 0.25,
                  color: isSortActive ? theme.palette.primary.main : 'text.secondary',
                }}
                aria-label={`Ordenar por ${column.label}`}
              >
                {isSortActive ? (
                  activeSortOrder === 'asc' ? (
                    <ArrowUpward sx={{ fontSize: 16 }} />
                  ) : (
                    <ArrowDownward sx={{ fontSize: 16 }} />
                  )
                ) : (
                  <SwapVert sx={{ fontSize: 16, opacity: 0.45 }} />
                )}
              </IconButton>
            ) : null}
            {column.filterable && filterKey ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleColumnFilterOpen(filterKey, e);
                }}
                sx={{
                  p: 0.25,
                  color: isFiltered ? theme.palette.primary.main : 'text.secondary',
                }}
                aria-label={`Filtrar ${column.label}`}
              >
                <FilterAlt sx={{ fontSize: 16 }} />
              </IconButton>
            ) : null}
          </Box>
        ) : null}
      </Box>
    );
  };

  // Renderizar célula
  const renderCell = (column: TableColumn, change: Change) => {
        const legislation = parseLegislationData(change.law);

    switch (column.id) {
      case 'state':
        return (
          <Chip
            label={change.state}
            size="small"
            color="primary"
            variant="outlined"
            sx={COMPACT_CHIP_SX}
          />
        );

      case 'agency':
        return (
          <Typography
            variant="body2"
            fontWeight={500}
            noWrap
            title={change.agency}
            sx={{ fontSize: '0.8125rem' }}
          >
            {change.agency}
          </Typography>
        );

      case 'lawType':
        return (
          <Chip
            label={change.law_type ?? legislation.type}
            size="small"
            color={getLegislationColor(change.law_type ?? legislation.type)}
            sx={{ ...COMPACT_CHIP_SX, fontWeight: 600 }}
          />
        );
      
      case 'lawNumber':
        return change.url ? (
          <Link
            href={change.url}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            onClick={(e) => e.stopPropagation()}
            sx={{ fontWeight: 700 }}
          >
            {legislation.number}
          </Link>
        ) : (
          <Typography variant="body2" fontWeight={600} color="primary">
            {legislation.number}
          </Typography>
        );
      
      case 'title':
        return (
          <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>
            {change.title}
          </Typography>
        );

      case 'detectedDate':
        return change.date ? (
          <Typography variant="body2" component="span" noWrap sx={{ fontSize: '0.8125rem' }}>
            {formatOfficialDateBR(change.date)}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
            N/A
          </Typography>
        );

      case 'publicationDate':
        return change.published_at ? (
          <Typography variant="body2" component="span" noWrap sx={{ fontSize: '0.8125rem' }}>
            {formatOfficialDateBR(change.published_at)}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
            N/A
          </Typography>
        );
      
      case 'effectiveDate':
        return change.vigency_status ? (
          <Chip
            label={change.vigency_status}
            size="small"
            color={change.vigency_status === 'Revogada' ? 'error' : 'success'}
            variant="outlined"
            sx={COMPACT_CHIP_SX}
          />
        ) : change.effective_date ? (
          formatOfficialDateBR(change.effective_date)
        ) : (
          <Typography variant="body2" color="text.secondary">
            N/A
          </Typography>
        );
      
      case 'changeType':
        return (
          <Chip
            label={change.change}
            size="small"
            color={getChangeTypeColor(change.change)}
            sx={COMPACT_CHIP_SX}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <Box>
      <Card>
        {quickEnabled && (
          <Box
            sx={{
              p: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'flex-end',
            }}
          >
            {showQuickNumber && (
              <TextField
                size="small"
                label="Número"
                value={quickNumberQuery}
                onChange={(e) => setQuickValues({ numberQuery: e.target.value })}
                placeholder="Ex.: 1.234/2024"
                sx={{ minWidth: 180 }}
              />
            )}
            {showQuickTitle && (
              <TextField
                size="small"
                label="Título"
                value={quickTitleQuery}
                onChange={(e) => setQuickValues({ titleQuery: e.target.value })}
                placeholder="Buscar no título..."
                sx={{ minWidth: 240, flex: '1 1 240px' }}
              />
            )}

            {showQuickPublicationRange && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                  Publicação
                </Typography>
                <TextField
                  size="small"
                  type="date"
                  label="De"
                  value={quickPublicationStart}
                  onChange={(e) => setQuickValues({ publicationStart: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 150 }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Até"
                  value={quickPublicationEnd}
                  onChange={(e) => setQuickValues({ publicationEnd: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 150 }}
                />
              </Box>
            )}

            {showQuickEffectiveRange && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                  Vigência
                </Typography>
                <TextField
                  size="small"
                  type="date"
                  label="De"
                  value={quickEffectiveStart}
                  onChange={(e) => setQuickValues({ effectiveStart: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 150 }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Até"
                  value={quickEffectiveEnd}
                  onChange={(e) => setQuickValues({ effectiveEnd: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 150 }}
                />
              </Box>
            )}

            <Box sx={{ flex: '1 1 auto' }} />
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setQuickValues({
                  numberQuery: '',
                  titleQuery: '',
                  publicationStart: '',
                  publicationEnd: '',
                  effectiveStart: '',
                  effectiveEnd: '',
                });
                clearAllColumnFilterSelections();
                if (pagination?.enabled) pagination.onPageChange(1);
              }}
            >
              Limpar filtros
            </Button>
          </Box>
        )}

        <TableContainer>
          <Table
            stickyHeader={stickyHeader}
            size={dense ? 'small' : 'medium'}
            sx={{ tableLayout: 'fixed', width: '100%' }}
          >
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.id} sx={getHeaderCellSx(column, dense)}>
                    {renderColumnHeader(column)}
                  </TableCell>
                ))}
                {showActions && (
                  <TableCell
                    align="center"
                    sx={{
                      width: ACTIONS_COLUMN_WIDTH,
                      minWidth: ACTIONS_COLUMN_WIDTH,
                      px: 1,
                      py: dense ? 0.75 : 1,
                      verticalAlign: 'top',
                      whiteSpace: 'nowrap',
                      textAlign: 'center',
                    }}
                  >
                    {actionsColumnFilters ? (
                      <ChangesActionsColumnHeader
                        monitoredNormAssuntoActive={actionsColumnFilters.monitoredNormAssuntoActive}
                        monitoredAgencyActive={actionsColumnFilters.monitoredAgencyActive}
                        greeningQaFilterActive={actionsColumnFilters.greeningQaActive}
                        onMonitoredNormAssuntoToggle={actionsColumnFilters.onMonitoredNormAssuntoToggle}
                        onMonitoredAgencyToggle={actionsColumnFilters.onMonitoredAgencyToggle}
                        onGreeningQaFilterToggle={actionsColumnFilters.onGreeningQaToggle}
                      />
                    ) : (
                      <Box component="strong" sx={{ ...HEADER_LABEL_SX, display: 'block', textAlign: 'center' }}>
                        Ações
                      </Box>
                    )}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length + (showActions ? 1 : 0)} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      Carregando...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : processedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + (showActions ? 1 : 0)} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      Nenhuma mudança encontrada
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                processedData.map((change) => (
                  <TableRow
                    key={change.id}
                    id={changeTableRowId(change.id)}
                    hover
                    onClick={() => onRowClick && onRowClick(change)}
                    sx={{
                      cursor: onRowClick ? 'pointer' : 'default',
                      '&:hover': {
                        bgcolor: onRowClick ? alpha(theme.palette.primary.main, 0.05) : 'inherit',
                      },
                      ...(highlightedChangeId === change.id && {
                        bgcolor: alpha(theme.palette.warning.main, 0.2),
                        outline: `2px solid ${theme.palette.warning.main}`,
                        outlineOffset: -2,
                      }),
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell key={column.id} sx={getBodyCellSx(column, dense)}>
                        {renderCell(column, change)}
                      </TableCell>
                    ))}
                    {showActions && (
                      <TableCell
                        align="center"
                        sx={{
                          width: ACTIONS_COLUMN_WIDTH,
                          minWidth: ACTIONS_COLUMN_WIDTH,
                          px: 1,
                          whiteSpace: 'nowrap',
                          textAlign: 'center',
                        }}
                      >
                        <ActionCell
                          item={change}
                          contentAlign="center"
                          monitoringStatus={monitoringStatus}
                          onViewDetails={onViewDetails}
                          onSendEmail={onSendEmail}
                          showMonitoringBadge={
                            hasMonitoringFlagsFromApi(change) || isChangeFromMonitoredLegislation(change)
                          }
                          monitoringBadges={
                            hasMonitoringFlagsFromApi(change) ? (
                              <ChangeMonitoringBadges
                                is_assunto_monitored={change.is_assunto_monitored}
                                monitored_assunto_label={change.monitored_assunto_label}
                                is_norm_monitored={change.is_norm_monitored}
                                is_keyword_monitored={change.is_keyword_monitored}
                                monitored_keyword_label={change.monitored_keyword_label}
                                is_agency_monitored={change.is_agency_monitored}
                                monitored_agency_label={change.monitored_agency_label}
                              />
                            ) : isChangeAssuntoMonitored(change) ? (
                              <ChangeMonitoringBadges
                                is_assunto_monitored
                                monitored_assunto_label={getMonitoredAssuntoLabel(change)}
                              />
                            ) : null
                          }
                          isAssuntoMonitored={false}
                          assuntoMonitoringLabel={null}
                          type="change"
                          monitoringHistory={[]}
                          isMonitored={isChangeFromMonitoredLegislation(change)}
                          modalProps={modalProps}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {pagination && pagination.enabled && (
          <TablePagination
            component="div"
            count={pagination.totalRows}
            page={pagination.page - 1}
            onPageChange={(_, page) => pagination.onPageChange(page + 1)}
            rowsPerPage={pagination.rowsPerPage}
            onRowsPerPageChange={(e) => pagination.onRowsPerPageChange(parseInt(e.target.value, 10))}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        )}
      </Card>

      {/* Menus de Filtro de Coluna */}
      {(
        [
          'state',
          'agency',
          'lawType',
          'detectedDate',
          'publicationDate',
          'effectiveDate',
          'changeType',
        ] as const
      ).map((column) => {
        const agencyHasCatalog = Boolean(
          agencyFilterCatalog &&
            ((agencyFilterCatalog.federacao?.length ?? 0) > 0 ||
              (agencyFilterCatalog.estados ?? []).some((g) => (g?.names?.length ?? 0) > 0) ||
              (agencyFilterCatalog.municipios ?? []).some((s) =>
                (s?.cities ?? []).some((c) => (c?.names?.length ?? 0) > 0),
              )),
        );
        const stateHasCatalog = Boolean(stateFilterOptions && stateFilterOptions.length > 0);
        const usesScopeTabs =
          (column === 'state' && stateHasCatalog) || (column === 'agency' && agencyHasCatalog);
        const showGroupedAgencyFilter = column === 'agency' && agencyHasCatalog && agencyFilterCatalog;

        let uniqueValues: string[] = getUniqueColumnValues(column);
        let lawTypeGroups: LawTypeOption[] = [];
        if (column === 'state' && stateFilterOptions && stateFilterOptions.length > 0) {
          uniqueValues =
            stateFilterTab === 'federacao'
              ? stateFilterOptions.filter((v) => v === 'Federal')
              : stateFilterTab === 'estados'
                ? stateFilterOptions.filter((v) => /^[A-Z]{2}$/.test(v))
                : stateFilterOptions.filter((v) => v !== 'Federal' && !/^[A-Z]{2}$/.test(v));
        } else if (column === 'lawType') {
          const raw = lawTypeFilterOptions && lawTypeFilterOptions.length > 0
            ? lawTypeFilterOptions
            : getUniqueColumnValues('lawType');
          lawTypeGroups = groupLawTypeOptions(raw);
          uniqueValues = lawTypeGroups.map((group) => group.label);
        } else if (column === 'changeType' && changeTypeFilterOptions && changeTypeFilterOptions.length > 0) {
          uniqueValues = changeTypeFilterOptions;
        }
        const selectedForColumn =
          column === 'state'
            ? (controlledStateFilter !== undefined ? controlledStateFilter : columnFilters.state)
            : columnFilters[column];
        const allSelected = selectedForColumn.length === 0;

        const columnLabel =
          column === 'state'
            ? 'Estado'
            : column === 'agency'
              ? 'Agência'
              : column === 'lawType'
                ? 'Tipo de Norma'
                : column === 'detectedDate'
                  ? 'Detectada em'
                  : column === 'publicationDate'
                    ? 'Data de Publicação'
                    : column === 'effectiveDate'
                      ? 'Vigência'
                      : 'Tipo de Mudança';
        
        return (
          <Menu
            key={column}
            anchorEl={columnFilterAnchor[column]}
            open={Boolean(columnFilterAnchor[column])}
            onClose={() => handleColumnFilterClose(column)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                maxHeight: 400,
                width: showGroupedAgencyFilter ? 520 : usesScopeTabs ? 440 : 280,
                mt: 1,
              },
            }}
          >
            <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Filtrar por {columnLabel}
              </Typography>
            </Box>
            {column === 'state' && stateHasCatalog && (
              <Box sx={{ px: 1.5, pt: 1, pb: 0.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Tabs
                  value={stateFilterTab}
                  onChange={(_, v) => setStateFilterTab(v)}
                  variant="fullWidth"
                  sx={{
                    minHeight: 40,
                    '& .MuiTab-root': { minWidth: 0, px: 1, fontSize: '0.8125rem', textTransform: 'none' },
                  }}
                >
                  <Tab value="federacao" label="Federação" />
                  <Tab value="estados" label="Estados" />
                  <Tab value="municipios" label="Municípios" />
                </Tabs>
              </Box>
            )}
            {showGroupedAgencyFilter && agencyFilterCatalog ? (
              <AgencyFilterPanel
                catalog={agencyFilterCatalog}
                selected={selectedForColumn}
                onChange={(vals) => setColumnFilters((prev) => ({ ...prev, agency: vals }))}
                onSelectAll={() => handleClearColumnFilter('agency')}
              />
            ) : (
            <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
              {/* Opção "Todos" */}
              <MenuItem
                onClick={() => handleClearColumnFilter(column)}
                sx={{ py: 1, bgcolor: allSelected ? alpha(theme.palette.primary.main, 0.1) : 'transparent' }}
              >
                <Box display="flex" alignItems="center" width="100%">
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      border: `2px solid ${theme.palette.primary.main}`,
                      borderRadius: 0.5,
                      mr: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: allSelected ? theme.palette.primary.main : 'transparent',
                    }}
                  >
                    {allSelected && (
                      <Check sx={{ fontSize: 16, color: 'white' }} />
                    )}
                  </Box>
                  <Typography variant="body2" fontWeight={allSelected ? 600 : 400}>
                    Todos
                  </Typography>
                </Box>
              </MenuItem>
              
              <Divider sx={{ my: 0.5 }} />

              {uniqueValues.map((value) => {
                  const lawGroup = column === 'lawType'
                    ? lawTypeGroups.find((group) => group.label === value)
                    : undefined;
                  const checked = lawGroup
                    ? lawGroup.values.some((item) => selectedForColumn.includes(item))
                    : selectedForColumn.includes(value);
                  return (
                  <MenuItem
                    key={value}
                    onClick={() => (lawGroup ? handleLawTypeToggle(lawGroup.values) : handleColumnFilterToggle(column, value))}
                    sx={{ py: 1 }}
                  >
                    <Box display="flex" alignItems="center" width="100%">
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          border: `2px solid ${theme.palette.primary.main}`,
                          borderRadius: 0.5,
                          mr: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: checked
                            ? theme.palette.primary.main
                            : 'transparent',
                        }}
                      >
                        {checked && (
                          <Check sx={{ fontSize: 16, color: 'white' }} />
                        )}
                      </Box>
                      <Typography variant="body2">
                        {column === 'detectedDate' || column === 'publicationDate' || column === 'effectiveDate'
                          ? formatDateMenuLabel(value)
                          : value}
                      </Typography>
                    </Box>
                  </MenuItem>
                  );
                })}
            </Box>
            )}
            <Box sx={{ p: 1, borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={() => handleClearColumnFilter(column)}
                fullWidth
                variant="outlined"
              >
                Limpar
              </Button>
              <Button
                size="small"
                onClick={() => handleColumnFilterClose(column)}
                fullWidth
                variant="contained"
              >
                Aplicar
              </Button>
            </Box>
          </Menu>
        );
      })}

    </Box>
  );
}

